import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, TextField, Button, Stack, Alert, Checkbox, FormControlLabel } from '@mui/material';
import notificationsApi from '../../services/api/notifications-advanced.api';

interface Props {
  open: boolean;
  onClose: () => void;
}

const BulkNotificationManager: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation('common');
  const [userIds, setUserIds] = useState('');
  const [roles, setRoles] = useState('');
  const [filters, setFilters] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [categoryKey, setCategoryKey] = useState('');
  const [type, setType] = useState('system');
  const [isImportant, _setIsImportant] = useState(false);
  const [actionUrl, setActionUrl] = useState('');
  const [actionText, setActionText] = useState('');
  const [channels, setChannels] = useState<string[]>(['inApp']);
  const [schedule, setSchedule] = useState('');
  const [dryRun, setDryRun] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggleChannel = (ch: string) => {
    setChannels(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]);
  };

  const handleSend = async () => {
    setLoading(true);
    setResult(null);
    try {
      const payload: any = {
        audience: {
          userIds: userIds ? userIds.split(',').map(s => s.trim()).filter(Boolean) : undefined,
          roles: roles ? roles.split(',').map(s => s.trim()).filter(Boolean) : undefined,
          filters: filters ? JSON.parse(filters) : undefined,
        },
        templateId: templateId || undefined,
        override: (title || message || categoryKey || type || isImportant || actionUrl || actionText) ? {
          title: title || undefined,
          message: message || undefined,
          categoryKey: categoryKey || undefined,
          type: type || undefined,
          isImportant: isImportant || undefined,
          actionUrl: actionUrl || undefined,
          actionText: actionText || undefined,
        } : undefined,
        channels: channels as any,
        schedule: schedule ? { when: schedule } : null,
        dryRun,
      };
      const res = await notificationsApi.sendBulkNotifications(payload);
      const data = (res as any).data || res;
      setIsError(false);
      setResult(t('notifications.bulkSendStarted', { jobId: data.jobId ?? '', accepted: data.accepted ?? 0 }));
    } catch (e: any) {
      setIsError(true);
      setResult(t('notifications.errorPrefix', { message: e?.message || t('notifications.errorUnknown') }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t('notificationUI.bulkSendTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={1} sx={{ mt: 1 }}>
          <TextField label={t('notifications.bulkAudienceUsers')} size="small" value={userIds} onChange={e => setUserIds(e.target.value)} fullWidth />
          <TextField label={t('notifications.bulkAudienceRoles')} size="small" value={roles} onChange={e => setRoles(e.target.value)} fullWidth />
          <TextField label={t('notifications.bulkFiltersJson')} size="small" value={filters} onChange={e => setFilters(e.target.value)} fullWidth />
          <TextField label={t('notifications.bulkTemplateId')} size="small" value={templateId} onChange={e => setTemplateId(e.target.value)} fullWidth />
          <TextField label={t('notifications.bulkTitleOverride')} size="small" value={title} onChange={e => setTitle(e.target.value)} fullWidth />
          <TextField label={t('notifications.bulkMessageOverride')} size="small" value={message} onChange={e => setMessage(e.target.value)} fullWidth multiline minRows={2} />
          <Stack direction="row" spacing={1}>
            <TextField label={t('notifications.bulkCategory')} size="small" value={categoryKey} onChange={e => setCategoryKey(e.target.value)} sx={{ width: 200 }} />
            <TextField label={t('notifications.bulkType')} size="small" value={type} onChange={e => setType(e.target.value)} sx={{ width: 200 }} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField label="Action URL" size="small" value={actionUrl} onChange={e => setActionUrl(e.target.value)} fullWidth />
            <TextField label="Action Text" size="small" value={actionText} onChange={e => setActionText(e.target.value)} sx={{ width: 200 }} />
          </Stack>
          <Box>
            <FormControlLabel control={<Checkbox checked={channels.includes('inApp')} onChange={() => toggleChannel('inApp')} />} label="inApp" />
            <FormControlLabel control={<Checkbox checked={channels.includes('push')} onChange={() => toggleChannel('push')} />} label="push" />
            <FormControlLabel control={<Checkbox checked={channels.includes('email')} onChange={() => toggleChannel('email')} />} label="email" />
            <FormControlLabel control={<Checkbox checked={channels.includes('sms')} onChange={() => toggleChannel('sms')} />} label="sms" />
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField label={t('notifications.bulkScheduleAt')} size="small" value={schedule} onChange={e => setSchedule(e.target.value)} fullWidth />
            <FormControlLabel control={<Checkbox checked={dryRun} onChange={e => setDryRun(e.target.checked)} />} label={t('notifications.bulkDryRunLabel')} />
          </Stack>
          {result && <Alert severity={isError ? 'error' : 'success'}>{result}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('close')}</Button>
        <Button variant="contained" onClick={handleSend} disabled={loading}>{t('notifications.send')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkNotificationManager;
